import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 40px 20px;
  background: #1a1a1a;
  color: #e0e0e0;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const HomeButton = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  background: rgba(0, 180, 219, 0.2);
  color: #00b4db;
  padding: 10px 20px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: bold;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  border: 1px solid #00b4db;
  z-index: 100;

  &:hover {
    background: rgba(0, 180, 219, 0.3);
    transform: translateY(-2px);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const MainTitle = styled.h1`
  font-size: 2.5em;
  color: #00b4db;
  margin-bottom: 20px;
`;

const Subtitle = styled.h2`
  font-size: 1.8em;
  margin-bottom: 30px;
  color: #00b4db;
  text-shadow: 0 0 10px rgba(0, 180, 219, 0.3);
`;

const Section = styled.section`
  margin: 40px 0;
  background: rgba(35, 35, 35, 0.6);
  padding: 25px;
  border-radius: 15px;
  border: 1px solid rgba(0, 180, 219, 0.1);
  backdrop-filter: blur(5px);

  h2 {
    color: #00b4db;
    margin-bottom: 15px;
    font-size: 1.5em;
  }

  p {
    line-height: 1.6;
    color: #e0e0e0;
  }

  ul {
    list-style: none;
    padding: 0;
    
    li {
      margin: 15px 0;
      padding-left: 25px;
      position: relative;

      &:before {
        content: "→";
        position: absolute;
        left: 0;
        color: #00b4db;
      }
    }
  }
`;

const PriceBox = styled.div`
  background: rgba(35, 35, 35, 0.8);
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  margin: 40px 0;
  border: 1px solid rgba(0, 180, 219, 0.2);
  backdrop-filter: blur(10px);

  .original-price {
    text-decoration: line-through;
    color: #888;
    font-size: 1.2em;
  }

  .current-price {
    font-size: 3em;
    color: #00b4db;
    text-shadow: 0 0 20px rgba(0, 180, 219, 0.4);
    margin: 10px 0;
  }
`;

const BuyButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #00b4db, #0083b0);
  color: white;
  padding: 15px 50px;
  border-radius: 30px;
  text-decoration: none;
  font-size: 1.4em;
  font-weight: bold;
  margin-top: 20px;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 5px 15px rgba(0, 180, 219, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 180, 219, 0.4);
  }
`;

interface ProductPageProps {
  title: string;
  subtitle: string;
  problem: string;
  solution: string;
  benefits: string[];
  price: number;
  originalPrice: number;
  buyLink: string;
}

export const ProductPage = ({
  title,
  subtitle,
  problem,
  solution,
  benefits,
  price,
  originalPrice,
  buyLink
}: ProductPageProps) => (
  <PageContainer>
    <HomeButton to="/">← Home</HomeButton>
    <ContentWrapper>
      <Header>
        <MainTitle>{title}</MainTitle>
        <Subtitle>{subtitle}</Subtitle>
      </Header>

      <Section>
        <h2>The Problem</h2>
        <p>{problem}</p>
      </Section>

      <Section>
        <h2>The Solution</h2>
        <p>{solution}</p>
      </Section>

      <Section>
        <h2>With this guide, you'll discover:</h2>
        <ul>
          {benefits.map((benefit, index) => (
            <li key={index}>{benefit}</li>
          ))}
        </ul>
      </Section>

      <PriceBox>
        <div className="original-price">${originalPrice.toFixed(2)}</div>
        <div className="current-price">${price.toFixed(2)}</div>
        <BuyButton href={buyLink}>👉 Buy Now</BuyButton>
      </PriceBox>
    </ContentWrapper>
  </PageContainer>
);
